import { useEffect, useState, useMemo } from "react";
import api from "../../api/axios";
import { FiUsers, FiUserCheck, FiShield, FiBriefcase } from "react-icons/fi";
import "../../assets/css/Users/UsersList.css";
import "../../assets/css/Common/stats_cards.css";
import DynamicTable from "../Common/DynamicTable";
import { usersColumns } from "./UsersColumns";
import UsersCreate from "./UsersCreate";
import UsersDrawer from "./UsersDrawer";

export default function UsersList() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [openCreate, setOpenCreate] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pageSize = 10;

  // =========================
  // FETCH USERS
  // =========================
  const fetchUsers = async () => {
    try {
      const res = await api.get(`/users`);
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const safeSearch = (search || "").toLowerCase().trim();
    if (!safeSearch) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(safeSearch) ||
        u.email?.toLowerCase().includes(safeSearch) ||
        u.role?.toLowerCase().includes(safeSearch) ||
        u.phone?.toLowerCase().includes(safeSearch) ||
        String(u.id || "").includes(safeSearch)
    );
  }, [users, search]);

const user = JSON.parse(sessionStorage.getItem("user"));

const isSuperAdmin =
  user?.role?.trim().toLowerCase() === "super admin";

  
const canDelete =
  user?.role !== "Sales Executive" &&
  user?.role !== "Sales Manager";


const openUser = async (id) => {
  try {
    const res = await api.get(`/users/${id}`);

    setSelectedUser(res.data);
    setDrawerOpen(true);

  } catch (error) {
    console.error("Failed to fetch user:", error);

    // Do nothing here
    // Axios interceptor already shows Swal
  }
};


  // =========================
  // STATS (derived locally)
  // =========================
const stats = useMemo(() => {
  return users.reduce(
    (acc, user) => {
      const role = user.role?.toLowerCase() || "";

      acc.total += 1;

      if (role.includes("manager")) {
        acc.managers += 1;
      } 
      else if (role.includes("admin")) {
        acc.admins += 1;
      } 
      else if (role.includes("executive")) {
        acc.executives += 1;
      }

      return acc;
    },
    {
      total: 0,
      managers: 0,
      admins: 0,
      executives: 0,
    }
  );
}, [users]);

  // =========================
  // CREATE SUCCESS
  // =========================
  const handleUserCreated = async () => {
    setOpenCreate(false);
    setPage(1);
    await fetchUsers(1);
  };

  // =========================
  // DELETE USER
  // =========================
  const handleDeleteUser = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      await fetchUsers(page);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  // =========================
  // BULK DELETE
  // =========================
const handleBulkDelete = async () => {
  try {
    await api.post("/users/bulk-delete", {
      ids: selectedIds,
    });

    setSelectedIds([]);
    await fetchUsers();
  } catch (err) {
    console.error(err);
  }
};

  //sorting logic

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const getSortValue = (row, key) => {
    switch (key) {
      case "user":
        return row.name || "";

      case "role":
        return row.role || "";

      case "assigned_leads":
        return Number(row.assigned_leads || 0);

      case "assigned_deals":
        return Number(row.assigned_deals || 0);

      case "created":
        return row.created_at
          ? new Date(row.created_at).getTime()
          : 0;

      default:
        return row[key];
    }
  };


  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;

    return [...filteredUsers].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortConfig.direction === "asc"
          ? aVal - bVal
          : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredUsers, sortConfig]);


  const handleSortChange = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

const handleSelect = (id) => {
  setSelectedIds((prev) =>
    prev.includes(id)
      ? prev.filter((item) => item !== id)
      : [...prev, id]
  );
};

const handleSelectAll = () => {
  if (selectedIds.length === sortedUsers.length) {
    setSelectedIds([]);
  } else {
    setSelectedIds(sortedUsers.map((user) => user.id));
  }
};

  // =========================
  // UI
  // =========================
  return (
    <div className="users-page">

      {/* HEADER */}
      <div className="users-header">
        <div>
          <h2 className="users-title">Users</h2>
          <p className="users-subtitle">
            Manage system users, roles, and permissions
          </p>
        </div>
      </div>

      {/* STATS */}
      <div className="stats-grid stats-grid-4">

        <div className="stat-card">
          <div className="stat-icon blue">
            <FiUsers />
          </div>
          <div>
            <p>Total Users</p>
            <h2>{stats.total}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">
            <FiUserCheck />
          </div>
          <div>
            <p>Managers</p>
            <h2>{stats.managers}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">
            <FiShield />
          </div>
          <div>
            <p>Admins</p>
            <h2>{stats.admins}</h2>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon gold">
            <FiBriefcase />
          </div>
          <div>
            <p>Executives</p>
            <h2>{stats.executives}</h2>
          </div>
        </div>

      </div>

      {/* TABLE */}
      <div className="users-card">
        <DynamicTable
          data={sortedUsers}
          columns={usersColumns({
  selectedIds,
  handleSelect,
  handleSelectAll,
  totalRows: sortedUsers.length,
})}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search users..."
          onRowClick={(id) => openUser(id)}
          serverMode={false}
          currentPage={page}
          totalPages={totalPages}
          selectedIds={selectedIds}
          selectedCount={selectedIds.length}
          totalRecords={totalRecords}
          pageSize={pageSize}
          onPageChange={setPage}
          canDelete={canDelete}
          onAdd={
            isSuperAdmin 
            ? () => setOpenCreate(true) 
            : undefined
          }
          addButtonText="Add User"
          storageKey="users_columns"
          onDelete={handleDeleteUser}
          onBulkDelete={handleBulkDelete}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
        //
        />
      </div>

      {/* CREATE MODAL */}
      {openCreate && (
        <UsersCreate
          onClose={() => setOpenCreate(false)}
          onSuccess={handleUserCreated}
        />
      )}

      <UsersDrawer
        user={selectedUser}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        refreshUsers={() => fetchUsers(page)}
      />
    </div>
  );
}